import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  Signal,
  ViewContainerRef,
  EventEmitter,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { FormBuilder } from '@angular/forms';

import { Store } from '@ngrx/store';
import { race, take } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types
import { TellentUserAbilitiesSelectors } from '@core/tellent-data'; // FIXME
import { TellentAppsDataFacade, TellentMembersDataFacade } from '@core/tellent-logic';
import {
  TellentMember,
  TellentOrgAbilities,
  MEMBER_LISTED_ONLY,
  SeatKind,
  TeamMemberStatus,
} from '@core/tellent-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogRef,
  DialogService,
  DialogWidth,
  HeadingComponent,
} from '@core/user-interface-legacy';
import { useProtectedControlsCallback } from '@core/user-interface-routing-logic';

import type { EditMemberForm } from './edit-member-permissions-access.service';
import { EditMemberPermissionsAccessService } from './edit-member-permissions-access.service';
import { EditMemberPermissionsBoxComponent } from './edit-member-permissions-box/edit-member-permissions-box.component';
import { EditMemberPermissionsDialogComponent } from './edit-member-permissions-dialog/edit-member-permissions-dialog.component';
import {
  AccessRolesTableComponent,
  SeatType,
} from '../../add-member-dialog/access-roles-table/access-roles-table.component';
import { AccessRolesTableService } from '../../add-member-dialog/access-roles-table/access-roles-table.service';

export type SeatTypeValue = {
  toggle: boolean | null;
  appGuid: string | null;
  roleExternalId: string | null;
};

/*
 eslint-disable
  @typescript-eslint/consistent-type-assertions,
 @angular-eslint/no-inputs-metadata-property,
*/

@Component({
  selector: 'edit-member-permissions-access-tab',
  imports: [
    AccessRolesTableComponent,
    ButtonComponent,
    DialogFooterComponent,
    TranslatePipe,
    DialogBodyComponent,
    ButtonLoadingDirective,
    EditMemberPermissionsBoxComponent,
    HeadingComponent,
  ],

  templateUrl: './edit-member-permissions-access-tab.component.html',
  styleUrls: ['./edit-member-permissions-access-tab.component.less'],
  inputs: EditMemberPermissionsAccessTabComponent.NgInputs,
})
export class EditMemberPermissionsAccessTabComponent {
  public static NgInputs = ['memberGuid' as const];

  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly editMemberAccessService = inject(EditMemberPermissionsAccessService);
  private readonly appsDataFacade = inject(TellentAppsDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly dialogService = inject(DialogService);
  private readonly viewRef = inject(ViewContainerRef);
  private readonly store = inject(Store);
  private readonly accessRolesTableSrv = inject(AccessRolesTableService);

  public memberUpdated = new EventEmitter<void>();
  public memberGuid: string;
  public destroyRef = inject(DestroyRef);
  public cdRef = inject(ChangeDetectorRef);

  public protectedControls = useProtectedControlsCallback();

  public pending = signal<boolean>(false);

  public memberData: Signal<TellentMember | null>;
  public canManageTellent: Signal<boolean | null>;

  public tellentApps = this.appsDataFacade.entities;

  public formGroup: FormGroup<EditMemberForm>;

  public disableForm = effect(() => {
    this.memberData()?.status === TeamMemberStatus.Deactivated
      ? this.formGroup.disable()
      : this.formGroup.enable();
  });

  public formUpdate = effect(() => {
    const formControls = this.formGroup.controls.seats.controls;
    formControls.recruitee.setValue(
      this.updateSeatFormGroup(SeatKind.Recruitment, this.memberData()),
    );
    formControls.kiwi.setValue(this.updateSeatFormGroup(SeatKind.CoreHR, this.memberData()));
  });

  public ngOnInit() {
    this.appsDataFacade.fetchCollection();

    this.memberData = this.membersDataFacade.teamMemberById(this.memberGuid);

    this.formGroup = this.formBuilder.group<EditMemberForm>({
      seats: this.formBuilder.group({
        recruitee: this.createSeatFormGroup(SeatKind.Recruitment, this.memberData()),
        kiwi: this.createSeatFormGroup(SeatKind.CoreHR, this.memberData()),
      }),
    });

    this.canManageTellent = this.store.selectSignal(
      TellentUserAbilitiesSelectors.hasOne(TellentOrgAbilities.ManageTellent),
    );
  }

  public ngAfterViewInit(): void {
    const seats = this.formGroup.controls.seats.controls;
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.Recruitment, seats.recruitee);
    this.accessRolesTableSrv.bindSeatLogic(SeatKind.CoreHR, seats.kiwi);
  }

  public get seats() {
    return this.formGroup?.controls?.seats?.controls;
  }

  public submit() {
    const payload = this.editMemberAccessService.resolveSeatsPayload(
      this.memberData()!,
      this.formGroup,
      this.tellentApps()!,
    );

    if (payload.length) {
      this.pending.set(true);
      this.membersDataFacade.manageSeats({ seats: payload });

      race(this.membersDataFacade.manageSeatsSuccess$, this.membersDataFacade.manageSeatsFailed$)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(({ payload }) => {
          this.formGroup.markAsPristine();
          const result = { success: !(payload instanceof HttpErrorResponse), payload };
          this.dialogRef.close(result);
        });
    }
  }

  public cancel() {
    this.dialogRef.close();
  }

  protected onStatusActionClicked(): void {
    const ref: DialogRef<EditMemberPermissionsDialogComponent> = this.dialogService.openCustom(
      EditMemberPermissionsDialogComponent,
      {
        width: DialogWidth.Small,
        closeGuard: () => ref.componentInstance.closeGuard(),
      },
      { memberId: this.memberGuid },
      this.viewRef,
    );
  }

  private createSeatFormGroup(
    seatKind: SeatKind,
    memberData: TellentMember | null,
  ): FormGroup<SeatType> {
    const seat = memberData!.seats.find(seat => seat.app.kind === seatKind);
    const isMemberListedOnly = SeatKind.CoreHR && seat?.role === null;
    const defaultRole = this.appsDataFacade.defaultRoleBySeatKind(seatKind);

    return this.formBuilder.group({
      toggle: [seat?.status === 'active'],
      appGuid: [seat?.app.guid!],
      roleExternalId: [
        !isMemberListedOnly
          ? (seat?.role?.externalId || defaultRole()?.externalId)!
          : MEMBER_LISTED_ONLY,
      ],
    });
  }

  private updateSeatFormGroup(seatKind: SeatKind, memberData: TellentMember | null): SeatTypeValue {
    const seat = memberData!.seats.find(seat => seat.app.kind === seatKind);
    const isMemberListedOnly = SeatKind.CoreHR && seat?.role === null;
    const defaultRole = this.appsDataFacade.defaultRoleBySeatKind(seatKind);

    const roleExternalId = seat
      ? !isMemberListedOnly
        ? (seat?.role?.externalId || defaultRole()?.externalId)!
        : MEMBER_LISTED_ONLY
      : null;

    return {
      toggle: seat?.status === 'active',
      appGuid: seat?.app.guid! || null,
      roleExternalId: roleExternalId,
    };
  }
}
